import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/ranking", label: "打榜观察" },
  { to: "/about", label: "关于我们" },
];

interface SiteHeaderProps {
  variant?: "solid" | "overlay";
}

export default function SiteHeader({ variant = "solid" }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const overlay = variant === "overlay" && !scrolled && !menuOpen && !searchOpen;

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(value ? `/ranking?q=${encodeURIComponent(value)}` : "/ranking");
  };

  return (
    <header
      className={[
        "w-full z-50",
        variant === "overlay" ? "fixed top-0 left-0 right-0" : "sticky top-0",
        overlay
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200",
      ].join(" ")}
    >
      {!overlay && (
        <div className="hidden lg:block bg-foreground-950 text-background-100">
          <div className="px-6 xl:px-10 h-9 flex items-center justify-between text-xs tracking-wide">
            <span className="flex items-center gap-2">
              <i className="ri-fire-line text-accent-400 w-4 h-4 flex items-center justify-center" />
              今日热议：投票曲线异常时段复盘 · 集资账目该公开到什么程度
            </span>
            <span className="flex items-center gap-4 text-background-300">
              <span>内容仅作现象观察与讨论</span>
              <span className="text-background-400">|</span>
              <span>2026 年 9 月 10 日</span>
            </span>
          </div>
        </div>
      )}

      <div className="px-4 md:px-6 xl:px-10">
        <div className="h-16 md:h-[70px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span
              className={[
                "w-9 h-9 flex items-center justify-center rounded-md",
                overlay ? "bg-background-50/15 text-background-50" : "bg-primary-500 text-background-50",
              ].join(" ")}
            >
              <i className="ri-vip-crown-2-line text-lg" />
            </span>
            <span className="leading-tight">
              <span
                className={[
                  "block font-heading text-lg font-bold tracking-tight",
                  overlay ? "text-background-50" : "text-foreground-950",
                ].join(" ")}
              >
                瓜圈显微镜
              </span>
              <span
                className={[
                  "block text-[11px] tracking-widest",
                  overlay ? "text-background-200" : "text-foreground-500",
                ].join(" ")}
              >
                投票打榜数据观察
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden md:flex items-center gap-1">
            {navLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "px-4 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap",
                    overlay
                      ? isActive
                        ? "text-background-50 bg-background-50/15"
                        : "text-background-100 hover:bg-background-50/10"
                      : isActive
                        ? "text-primary-700 bg-primary-100"
                        : "text-foreground-700 hover:bg-background-100",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="搜索内容"
              onClick={() => setSearchOpen((v) => !v)}
              className={[
                "w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer",
                overlay ? "text-background-50 hover:bg-background-50/15" : "text-foreground-700 hover:bg-background-100",
              ].join(" ")}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <Link
              to="/about#subscribe"
              className="hidden sm:inline-flex items-center px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
            >
              订阅吃瓜日报
            </Link>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className={[
                "md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer",
                overlay ? "text-background-50 hover:bg-background-50/15" : "text-foreground-800 hover:bg-background-100",
              ].join(" ")}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-3" role="search">
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3">
              <i className="ri-search-line text-foreground-500 w-5 h-5 flex items-center justify-center" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索票数异常、集资账目、赛制争议…"
                className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="text-sm font-medium text-primary-600 whitespace-nowrap cursor-pointer px-2"
              >
                搜索
              </button>
            </div>
          </form>
        )}

        {menuOpen && (
          <nav aria-label="移动端导航" className="md:hidden pb-4 border-t border-background-200 pt-3">
            <ul className="flex flex-col gap-1">
              {navLinks.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      [
                        "block px-3 py-2.5 rounded-md text-sm cursor-pointer",
                        isActive ? "bg-primary-100 text-primary-700 font-medium" : "text-foreground-700",
                      ].join(" ")
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            <Link
              to="/about#subscribe"
              onClick={() => setMenuOpen(false)}
              className="mt-3 flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-medium bg-primary-500 text-background-50 whitespace-nowrap cursor-pointer"
            >
              订阅吃瓜日报
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}